import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { FadeUp, rich } from "./Reveal";

const SPEED = 48; // px al secondo — scorrimento lento ed elegante

export default function Portfolio() {
  const { t } = useLang();
  const navigate = useNavigate();
  const trackRef = useRef(null);
  const x = useRef(0);
  const half = useRef(0);
  const paused = useRef(false);
  const visible = useRef(true);

  const items = [...t.portfolio.items, ...t.portfolio.items];

  useEffect(() => {
    const measure = () => {
      if (trackRef.current) half.current = trackRef.current.scrollWidth / 2;
    };
    measure();
    window.addEventListener("resize", measure);

    let raf;
    let last = performance.now();
    const step = (now) => {
      const dt = Math.min(now - last, 64);
      last = now;
      if (!paused.current && visible.current && half.current > 0) {
        x.current += (SPEED * dt) / 1000;
        if (x.current >= half.current) x.current -= half.current;
        trackRef.current.style.transform = `translate3d(${-x.current}px,0,0)`;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);

    const io = new IntersectionObserver(([e]) => (visible.current = e.isIntersecting), { threshold: 0.05 });
    if (trackRef.current) io.observe(trackRef.current);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  const pause = () => (paused.current = true);
  const resume = () => (paused.current = false);

  const open = (p) => {
    sessionStorage.setItem("gg_home_scroll", String(window.scrollY));
    navigate(`/progetti/${p.slug}`);
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-28 md:py-40">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="mb-16">
          <FadeUp>
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 bg-brass" />
              <p className="eyebrow">{t.portfolio.overline}</p>
            </div>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-cream">
              {rich(t.portfolio.title)}
            </h2>
          </FadeUp>
        </div>
      </div>

      <div
        data-testid="portfolio-strip"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onPointerDown={pause}
        onPointerUp={resume}
      >
        <div ref={trackRef} role="list" className="flex w-max will-change-transform">
          {items.map((p, i) => (
            <FadeUp key={`${p.slug}-${i}`} delay={Math.min(i * 0.06, 0.3)} className="shrink-0 pr-5 sm:pr-8">
              <article
                role="listitem"
                data-testid={i < t.portfolio.items.length ? `portfolio-strip-item-${i + 1}` : undefined}
                data-hover
                onClick={() => open(p)}
                className="group relative w-[80vw] sm:w-[460px] lg:w-[600px] h-[400px] sm:h-[480px] lg:h-[560px] overflow-hidden cursor-pointer"
              >
                <img
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  src={p.img}
                  alt={`${p.title} — ${p.location}`}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-[filter,transform] duration-700 ease-out"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 bg-black/30 lg:bg-transparent lg:group-hover:bg-black/35 transition-colors duration-500">
                  <div className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 translate-y-2 lg:group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                    <h3 className="font-serif font-light text-2xl sm:text-3xl text-cream">{p.title}</h3>
                    {p.location && (
                      <p className="mt-3 text-[11px] uppercase tracking-[0.2em] text-cream/80">{p.location}</p>
                    )}
                    <span className="mt-4 inline-block h-px w-10 bg-brass" />
                  </div>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}
